/**
 * AssetStore (spec--pdfmarkuptool-image-markup 4.2, slice I2): where a photo's bytes live. The interface and its
 * policy (eviction, quota, the pending capture request's expiry, the status line) are here and pure; the local
 * adapter is IdbAssetAdapter.ts (IndexedDB `pmt-assets`). The remote adapter and the upload queue are slice I9.
 *
 * M1: the app never deletes a photo that has not left the device. The one eviction rule ({@link selectEvictions})
 * picks only rows with a remote copy, and `pmt-assets` is its own database, so the PDF cache's LRU
 * (PdfCacheService, `pdf-cache`) cannot reach a photo.
 */
import type { AssetMeta, IngestAssetStore } from '@/core/image/ingest';
import type { MediaRequest } from '@/core/image/mediaRequest';

export type { AssetMeta };

/** The asset database. Separate from `pdf-cache` on purpose (GR C4): never merge them. */
export const ASSET_DB_NAME = 'pmt-assets';

/** A stored asset (spec 3.3). `uploaded`: the projects whose PROMOTED copy holds it; empty = only on this device. */
export interface AssetRow extends AssetMeta {
    createdAt: number;
    lastUsedAt: number;
    uploaded: Record<string, true>;
}

export interface AssetRecord extends AssetRow { blob: Blob }

/** What Ingest hands to put (spec 4.2): the bytes and their meta; the store stamps the rest. */
export type AssetPut = Omit<AssetRecord, 'createdAt' | 'lastUsedAt' | 'uploaded'>;

/**
 * The camera hand-off (spec 4.3, PR M1): saved before the file input opens, so a placement survives iOS
 * discarding the tab while the full-screen camera is up. I6 writes it and answers it; I2 only stores it.
 */
export interface PendingMediaRequest extends MediaRequest {
    /** For the "Your photo at P12 on A101 was not received" prompt. */
    sheetLabel?: string;
    /** ms epoch when the input was opened: the record expires {@link PENDING_MAX_AGE_MS} after it. */
    openedAt: number;
}

/**
 * How long an unanswered capture request stays live. The record exists for ONE case: the tab was discarded while
 * the camera was up, and the reload happens as the user comes back from it -- seconds to a minute or two after the
 * input opened (iPadOS 18.3 probe: pickers answered within 9 s; 7 camera round trips, none near a minute). A record
 * older than this lost its answer instead: the probe saw a marker from a gesture that opened nothing, and Safari
 * sometimes answers a dismissal with only a window `focus` (no change / cancel), read back on a later PLAIN
 * reload as a false "photo not received" (2 of 2 reloads). Ten minutes covers a slow shot with margin and keeps
 * a reload after a break from prompting about a photo nobody is waiting for.
 */
export const PENDING_MAX_AGE_MS = 10 * 60_000;
/** A record stamped this far in the future (the clock moved back) is not trusted either. */
const PENDING_CLOCK_SKEW_MS = 60_000;

/** The default cap above which {@link AssetStore.evictUploaded} runs (spec 4.2: 500 MB). */
export const EVICT_CAP_MB = 500;
/** At this share of the quota the status line warns instead of counting (spec 4.2). */
export const NEARLY_FULL = 0.8;

export interface AssetUsage {
    /** Bytes of every stored asset. */
    assetsMb: number;
    /** The origin's usage (navigator.storage.estimate), 0 when unknown. */
    usedMb: number;
    /** The origin's quota, 0 when unknown. */
    quotaMb: number;
    persisted: boolean;
    /** Assets with no remote copy anywhere. */
    localOnly: number;
}

export interface PendingUploads { count: number }

/**
 * The 4.2 interface minus `enqueueUpload` (I9 adds it with the remote adapter and the queue). A structural
 * superset of Ingest's {@link IngestAssetStore}.
 */
export interface AssetStore extends IngestAssetStore {
    /** Durable when it resolves, or rejects with {@link QuotaError} (M1). */
    put(rec: AssetPut): Promise<void>;
    has(id: string): Promise<boolean>;
    /** The local bytes (I9: else download + verify sha256). */
    get(id: string, ctx: { projectId?: string }): Promise<Blob>;
    /** An object URL, ref-counted: one per asset while held; {@link release} revokes it with the last holder. */
    url(id: string): Promise<string>;
    release(id: string): void;
    /** Subscribe to the count of assets with no remote copy; called at once and on every change. */
    pending(listener: (p: PendingUploads) => void): () => void;
    usage(): Promise<AssetUsage>;
    /** LRU over rows WITH a remote copy only, down to `capMb`. */
    evictUploaded(capMb: number): Promise<{ deleted: number }>;
    savePending(req: PendingMediaRequest): Promise<void>;
    /** The saved request, removed in the same read; null when none or expired ({@link isPendingLive}). */
    takePending(): Promise<PendingMediaRequest | null>;
}

/** The store is full: the bytes are NOT stored. Ingest turns it into `quota` + salvage (4.1). */
export class QuotaError extends Error {
    override readonly name = 'QuotaError';
    constructor(readonly bytes: number, readonly cause?: unknown) {
        super('Storage is full: the photo could not be saved on this device');
    }
}

/** A full origin, however the browser reported it (a DOMException on the request or the aborted transaction). */
export function isQuotaExceeded(e: unknown): boolean {
    if (e instanceof QuotaError) return true;
    const name = (e as { name?: unknown } | null)?.name;
    return name === 'QuotaExceededError';
}

export function hasRemoteCopy(row: Pick<AssetRow, 'uploaded'>): boolean {
    return Object.values(row.uploaded ?? {}).some((v) => v === true);
}

/**
 * The one eviction rule (spec 4.2, SR C12): least recently used first, ONLY rows with a remote copy, until the
 * total fits `capBytes`. A row on this device only is never selected, even if the total then stays above the cap.
 */
export function selectEvictions(rows: readonly AssetRow[], capBytes: number): string[] {
    let total = rows.reduce((s, r) => s + r.bytes, 0);
    const out: string[] = [];
    const candidates = rows.filter(hasRemoteCopy).sort((a, b) => a.lastUsedAt - b.lastUsedAt);
    for (const r of candidates) {
        if (total <= capBytes) break;
        out.push(r.id);
        total -= r.bytes;
    }
    return out;
}

/** True while a saved capture request may still be answered by a reload (see {@link PENDING_MAX_AGE_MS}). */
export function isPendingLive(req: Pick<PendingMediaRequest, 'openedAt'>, now: number): boolean {
    const t = req.openedAt;
    if (typeof t !== 'number' || !Number.isFinite(t)) return false;
    return t <= now + PENDING_CLOCK_SKEW_MS && now - t <= PENDING_MAX_AGE_MS;
}

/**
 * The one status line (spec 4.2, SR C14). `tier: 'local'` = a local workspace, which never uploads, so its only
 * backup is an export. `device` is wording only ("iPad" on an iPad, else "device").
 */
export function storageStatusText(s: {
    localOnly: number; usedMb: number; quotaMb: number; tier: 'local' | 'cloud'; device: 'iPad' | 'device';
    /** The cloud upload queue's parked rows (uploadQueue.ts): they outrank the count. */
    parked?: { failed: number; forbidden: number; damaged: number };
}): string | null {
    const parked = s.tier === 'cloud' ? s.parked : undefined;
    if (parked && parked.forbidden > 0) return `No edit access to this project: photos kept on this ${s.device}`;
    if (parked && parked.damaged > 0) return `${parked.damaged} ${parked.damaged === 1 ? 'photo' : 'photos'} damaged on this ${s.device}: export them, then add them again`;
    // the Retry button (StorageStatusNotice) completes the line: "Upload failed -- Retry"
    if (parked && parked.failed > 0) return 'Upload failed --';
    if (s.localOnly <= 0) return null;
    if (s.quotaMb > 0 && s.usedMb >= NEARLY_FULL * s.quotaMb) {
        return `${s.device === 'iPad' ? 'iPad' : 'Device'} storage nearly full: export or sync photos`;
    }
    const count = `${s.localOnly} ${s.localOnly === 1 ? 'photo' : 'photos'} only on this ${s.device}`;
    return s.tier === 'local' ? `${count} -- Export PDF to back up` : count;
}
